import { BLOCK_GP_MASTER, GP_NAME_ALIASES } from '../constants/field-execution-master-data';
import { FIELD_EXECUTION_BLOCKS } from '../services/field-execution.service';

export interface GpValidationIssue {
    sheetName: string;
    gpNameRaw: string;
    message: string;
    suggestion?: string;
}

export interface GpValidationResult {
    valid: boolean;
    issues: GpValidationIssue[];
    /** lowercase raw GP label → canonical master GP name */
    resolvedNames: Map<string, string>;
}

export interface ImportPreValidation {
    canImport: boolean;
    errors: string[];
    gpValidation: GpValidationResult | null;
    duplicateCount: number;
    duplicateSamples: string[];
}

function toTitleCase(s: string): string {
    return s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

/** Normalise a GP label for alias / master lookup. */
export function normalizeGpLookupKey(name: string): string {
    return (name || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/** Strip trailing " GP" / " NAC" suffixes from sheet tab or header labels. */
export function stripGpSuffix(name: string): string {
    return name.replace(/\s+(gp|nac)\s*$/i, '').trim();
}

/**
 * Resolve a raw GP name to the canonical master-list name for a block.
 * Returns null when the GP is not recognised (validation must fail).
 */
export function resolveCanonicalGpName(gpNameRaw: string, block: string): string | null {
    const master = BLOCK_GP_MASTER[block];
    if (!master?.length) return null;

    const raw = (gpNameRaw || '').trim();
    if (!raw) return null;

    const key = normalizeGpLookupKey(raw);
    const strippedKey = normalizeGpLookupKey(stripGpSuffix(raw));

    const aliasHit = GP_NAME_ALIASES[key] ?? GP_NAME_ALIASES[strippedKey];
    if (aliasHit && master.includes(aliasHit)) return aliasHit;

    const candidates = [
        raw,
        stripGpSuffix(raw),
        toTitleCase(stripGpSuffix(raw)),
        toTitleCase(raw),
    ];
    for (const c of candidates) {
        const hit = master.find(g => g.toLowerCase() === c.toLowerCase());
        if (hit) return hit;
    }

    return null;
}

/** Collect unique GP names that must be validated for a parsed sheet. */
export function collectGpNamesFromSheet(sheet: {
    sheetName: string;
    gpName?: string;
    format: string;
    gpRows: { gpName: string }[];
    projectRows: { gpName: string }[];
}): { sheetName: string; gpNameRaw: string }[] {
    const names: { sheetName: string; gpNameRaw: string }[] = [];

    if (sheet.format === 'gp-per-sheet') {
        const gp = sheet.gpName || sheet.sheetName;
        names.push({ sheetName: sheet.sheetName, gpNameRaw: gp });
        return names;
    }

    const seen = new Set<string>();
    const rows = sheet.format === 'abstract' ? sheet.gpRows : sheet.projectRows;
    for (const row of rows) {
        const k = normalizeGpLookupKey(row.gpName);
        if (!k || seen.has(k)) continue;
        seen.add(k);
        names.push({ sheetName: sheet.sheetName, gpNameRaw: row.gpName });
    }
    return names;
}

/** Validate all GP names in parsed sheets against the master list for the selected block. */
export function validateGpMasterForBlock(
    sheets: Array<{
        sheetName: string;
        blockName: string;
        gpName?: string;
        format: string;
        gpRows: { gpName: string }[];
        projectRows: { gpName: string }[];
    }>,
    block: string
): GpValidationResult {
    const issues: GpValidationIssue[] = [];
    const resolvedNames = new Map<string, string>();
    const master = BLOCK_GP_MASTER[block];

    if (!master?.length) {
        return {
            valid: false,
            issues: [{
                sheetName: '—',
                gpNameRaw: block,
                message: `No GP master list is configured for block "${block}". Import cannot proceed.`,
            }],
            resolvedNames,
        };
    }

    if (!(FIELD_EXECUTION_BLOCKS as readonly string[]).includes(block)) {
        return {
            valid: false,
            issues: [{
                sheetName: '—',
                gpNameRaw: block,
                message: `"${block}" is not a recognised Field Execution block.`,
            }],
            resolvedNames,
        };
    }

    for (const sheet of sheets) {
        for (const { sheetName, gpNameRaw } of collectGpNamesFromSheet(sheet)) {
            const lookupKey = normalizeGpLookupKey(gpNameRaw);
            if (resolvedNames.has(lookupKey)) continue;

            const canonical = resolveCanonicalGpName(gpNameRaw, block);
            if (canonical) {
                resolvedNames.set(lookupKey, canonical);
            } else {
                const nearby = master
                    .filter(g => g.toLowerCase().startsWith(stripGpSuffix(gpNameRaw).toLowerCase().slice(0, 3)))
                    .slice(0, 3);
                issues.push({
                    sheetName,
                    gpNameRaw,
                    message: `"${gpNameRaw}" is not a valid GP for ${block} Block.`,
                    suggestion: nearby.length
                        ? `Did you mean: ${nearby.join(', ')}?`
                        : 'Check spelling — suffixes like " GP" are not allowed unless the GP exists in master data.',
                });
            }
        }
    }

    return { valid: issues.length === 0, issues, resolvedNames };
}

/** Apply canonical GP names to parsed sheet data (never creates new GPs). */
export function applyResolvedGpNames(
    sheets: Array<{
        gpName?: string;
        format: string;
        gpRows: { gpName: string }[];
        projectRows: { gpName: string }[];
    }>,
    block: string,
    resolved: Map<string, string>
): void {
    for (const sheet of sheets) {
        if (sheet.format === 'gp-per-sheet' && sheet.gpName) {
            const key = normalizeGpLookupKey(sheet.gpName);
            sheet.gpName = resolved.get(key) ?? resolveCanonicalGpName(sheet.gpName, block) ?? sheet.gpName;
        }
        for (const row of sheet.gpRows) {
            const key = normalizeGpLookupKey(row.gpName);
            row.gpName = resolved.get(key) ?? resolveCanonicalGpName(row.gpName, block) ?? row.gpName;
        }
        for (const row of sheet.projectRows) {
            const key = normalizeGpLookupKey(row.gpName);
            row.gpName = resolved.get(key) ?? resolveCanonicalGpName(row.gpName, block) ?? row.gpName;
        }
    }
}
